import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useColor from "@/hooks/useColor";
import {getCssSizeValue, isReactRef} from "@/utils/utils";
import {BREAKPOINTS} from "@/styles/themeSettings";

const Container = styled.div`
	display: flex;
	flex-direction: column;
    padding: 0;
    width: 100%;
    background-color: white;
    color: ${({theme}) => theme.colors.richBlack};
    text-align: left;
    box-sizing: border-box;
	gap: ${getCssSizeValue(7, BREAKPOINTS.MOBILE)};
    input {
        border: 1px solid ${({theme}) => theme.colors.richBlack}!important;
    }

    aside {
        padding-bottom: 6px;
    }

    @media (min-width: ${({theme}) => theme.breakpoints.desktop}px) and (orientation: landscape) {
		width: ${getCssSizeValue(605, BREAKPOINTS.DESKTOP)};
        gap: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
	}
	
`
const InputWrapper = styled.div`
	border: solid 1px ${({theme}) => theme.colors.richBlack};
    padding: ${getCssSizeValue(7, BREAKPOINTS.MOBILE)} ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};

    @media (min-width: ${({theme}) => theme.breakpoints.desktop}px) and (orientation: landscape) {
        padding: ${getCssSizeValue(14, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(16, BREAKPOINTS.DESKTOP)};
    }
`

const Details = styled.div`
	display: flex;
	width: 100%;
	gap: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};
`

const Detail = styled.div`
    width: 100%;

    .expiration {
        float: left;
    }

    .cvv {
        float: right;
    }
    @media (min-width: ${({theme}) => theme.breakpoints.desktop}px) and (orientation: landscape) {
		width: 180px;
    }
`
const PayUForm = ({ showForm, tokenize, locale, posId }) => {
	const [payuSdkForms, setPayuSdkForms] = useState(null);
	const [secureForms, setSecureForms] = useState(null);
	const formRef = useRef(null);
	const numberTitle = locale === 'pl' ? 'Numer karty' : 'Card Number';
	const numberPlaceholder = locale === 'pl' ? 'Nr karty' : 'Card Number';
	const validTitle = locale === 'pl' ? 'Data ważności' : 'Valid Thru';
	const validTPlaceholder = locale === 'pl' ? 'MM/YY' : 'MM/YY';
	const cvvTitle = locale === 'pl' ? 'CVV' : 'CVV';
	const cvvPlaceholder = locale === 'pl' ? 'CVV' : 'CVV';
	const errorColor = useColor('barnRed')
	const textColor = useColor('richBlack')
	const [formParamsCorrect, setFormParamsCorrect] = useState({
		card: false,
		date: false,
		cvv: false
	})
	const handleFormParamState = (param='card', error=true) => setFormParamsCorrect((prev) => ({ ...prev, [param]: Boolean(!error) }))

	useEffect(() => {
		if (!showForm || !posId) return;

		async function initPayU() {
			console.log("Initializing PayU with posId:", posId);
			if (secureForms) { // Destroy previous SecureForms instance if it exists
				try {
					console.log("Destroying existing SecureForms...");
					secureForms.destroy();
				} catch (error) {
					console.warn("Error destroying SecureForms:", error);
				}
				setSecureForms(null);
			}

			if (!window.PayU) { // Ensure PayU SDK is loaded only once
				console.log("Loading PayU SDK...");
				const script = document.createElement('script');
				script.src = "https://secure.snd.payu.com/javascript/sdk";
				script.async = true;
				script.onload = () => setupPayU();
				document.body.appendChild(script);
			} else {
				setupPayU();
			}
		}

		function setupPayU() {
			// console.log("Setting up PayU SDK for POS ID:", posId);
			if (secureForms) {
				console.log("Destroying previous SecureForms instance...");
				try {
					secureForms.destroy(); // Destroy old SecureForms instance if it exists
				} catch (error) {
					console.warn("Error destroying SecureForms:", error);
				}
				setSecureForms(null);
			}

			// 2️⃣ **Clear form container to avoid duplicate rendering**
			document.getElementById("payu-card-number")?.replaceChildren();
			document.getElementById("payu-card-date")?.replaceChildren();
			document.getElementById("payu-card-cvv")?.replaceChildren();

			const sdkForms = window.PayU(posId, { dev: true }); // Create a new SecureForms instance
			setPayuSdkForms(sdkForms);

			const newSecureForms = sdkForms.secureForms();
			setSecureForms(newSecureForms); // SecureForms initialized

			// 4️⃣ **Ensure elements exist before rendering (to avoid `element.not.exists`)**
			const waitForElements = setInterval(() => {
				const numberField = document.getElementById('payu-card-number');
				const dateField = document.getElementById('payu-card-date');
				const cvvField = document.getElementById('payu-card-cvv');

				if (numberField && dateField && cvvField) {
					clearInterval(waitForElements);

					console.log("Rendering SecureForm fields...");

					const optionsForms = {
						cardIcon: true,
						style: {
							basic: {
								fontColor: textColor,
								fontSize: '18px',
								fontWeight: '500',
							},
							invalid: {
								fontColor: errorColor
							},
						},
						placeholder: {
							number: numberPlaceholder,
							date: validTPlaceholder,
							cvv: cvvPlaceholder,
						},
						lang: 'pl',
					};

					const cardNumber = newSecureForms.add('number', optionsForms);
					const cardDate = newSecureForms.add('date', optionsForms);
					const cardCvv = newSecureForms.add('cvv', optionsForms);
					cardNumber.render('#payu-card-number');
					cardDate.render('#payu-card-date');
					cardCvv.render('#payu-card-cvv');
					cardNumber.on('change', ({ error }) => handleFormParamState('card', error))
					cardDate.on('change', ({ error }) => handleFormParamState('date', error))
					cardCvv.on('change', ({ error }) => handleFormParamState('cvv', error))
				}
			}, 100);
		}

		initPayU();
	}, [posId, showForm]);

	async function handleTokenize() {
		if (!payuSdkForms || !secureForms) {
			console.error('PayU SDK is not initialized. Please try again.');
			return;
		}
		try {
			const result = await payuSdkForms.tokenize('MULTI');
			if (result.status === 'SUCCESS') {
				return result.body.token;
			} else {
				console.error('Tokenization failed');
				return '';
			}
		} catch (error) {
			console.error('Tokenization failed:', error);
		}
	}

	useEffect(() => {
		if(!isReactRef(tokenize)) return
		tokenize.current = handleTokenize
	},[posId, secureForms])

	return (
		<Container ref={formRef} className="container">
			<InputWrapper id="payu-card-number"></InputWrapper>
			<Details className="card-details clearfix" >
				<Detail className="expiration">
					<InputWrapper id="payu-card-date"></InputWrapper>
				</Detail>
				<Detail className="cvv">
					<InputWrapper id="payu-card-cvv"></InputWrapper>
				</Detail>
			</Details>
		</Container>
	);
};

export default PayUForm;
